import React, { useCallback, useEffect, useState } from 'react';
import { useTenant } from '../../contexts/TenantContext';
import ScorecardNavigation from '../restaurant/ScorecardNavigation';
import {
  listPhraseRules,
  createPhraseRule,
  updatePhraseRule,
  deletePhraseRule,
  PhraseRule,
  Polarity,
} from '../../services/phraseRulesAdminService';

// ============================================================
// One row
// ============================================================

const RuleRow: React.FC<{
  rule: PhraseRule;
  onChanged: () => void;
}> = ({ rule, onChanged }) => {
  const [draft, setDraft] = useState<PhraseRule>(rule);
  const [saving, setSaving] = useState(false);

  useEffect(() => setDraft(rule), [rule]);

  const dirty = JSON.stringify(draft) !== JSON.stringify(rule);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updatePhraseRule(rule.id, {
        phrase: draft.phrase,
        polarity: draft.polarity,
        light_bulb_text: draft.light_bulb_text,
        is_active: draft.is_active,
      });
      onChanged();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete phrase rule "${rule.phrase}"?`)) return;
    await deletePhraseRule(rule.id);
    onChanged();
  };

  const polarityColors =
    draft.polarity === 'negative'
      ? 'border-red-200 bg-red-50/50'
      : 'border-green-200 bg-green-50/50';

  return (
    <div className={`border rounded-lg p-4 ${polarityColors}`}>
      <div className="flex items-start gap-3 mb-3">
        <div className="flex-1">
          <label className="text-xs font-medium text-inkMuted">Phrase (case-insensitive, paraphrase counts as a hit)</label>
          <textarea
            className="mt-1 border rounded px-2 py-1 text-sm w-full bg-white"
            rows={2}
            value={draft.phrase}
            onChange={(e) => setDraft({ ...draft, phrase: e.target.value })}
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs font-medium text-inkMuted">Polarity</label>
          <div className="flex gap-1">
            {(['negative', 'positive'] as Polarity[]).map((p) => (
              <button
                key={p}
                onClick={() => setDraft({ ...draft, polarity: p })}
                className={`px-3 py-1 rounded text-xs font-medium border ${
                  draft.polarity === p
                    ? p === 'negative'
                      ? 'bg-red-600 text-white border-red-600'
                      : 'bg-green-600 text-white border-green-600'
                    : 'bg-white text-ink border-softBorder hover:bg-gray-50'
                }`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
      </div>

      <label className="text-xs font-medium text-inkMuted">Coaching note (shown to admins on a hit)</label>
      <textarea
        className="mt-1 border rounded px-2 py-1 text-sm w-full bg-white"
        rows={2}
        value={draft.light_bulb_text}
        onChange={(e) => setDraft({ ...draft, light_bulb_text: e.target.value })}
      />

      <div className="mt-3 flex items-center justify-between gap-2">
        <label className="text-xs flex items-center gap-2 text-inkMuted">
          <input
            type="checkbox"
            checked={draft.is_active}
            onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })}
          />
          Active (included in the LLM prompt)
        </label>
        <div className="flex gap-2">
          <button
            onClick={handleDelete}
            className="text-sm text-coral hover:text-red-700"
          >
            Delete
          </button>
          <button
            onClick={handleSave}
            disabled={!dirty || saving}
            className="px-3 py-1 rounded bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-40"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// New-rule form
// ============================================================

const NewRuleForm: React.FC<{
  tenantId: string;
  onCreated: () => void;
}> = ({ tenantId, onCreated }) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<{
    phrase: string;
    polarity: Polarity;
    light_bulb_text: string;
  }>({ phrase: '', polarity: 'negative', light_bulb_text: '' });
  const [saving, setSaving] = useState(false);

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="px-3 py-2 rounded border border-dashed border-gray-300 text-sm text-inkMuted hover:bg-white w-full"
      >
        + Add phrase
      </button>
    );
  }

  const canSave = draft.phrase.trim() && draft.light_bulb_text.trim();

  const handleCreate = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await createPhraseRule(tenantId, {
        ...draft,
        is_active: true,
      });
      setDraft({ phrase: '', polarity: 'negative', light_bulb_text: '' });
      setOpen(false);
      onCreated();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border-2 border-dashed border-blue-200 rounded-lg p-4 bg-blue-50">
      <div className="text-xs font-medium text-blue-700 mb-2">New phrase rule</div>
      <textarea
        className="border rounded px-2 py-1 text-sm w-full mb-2 bg-white"
        rows={2}
        placeholder='Phrase (e.g. "is this your first time")'
        value={draft.phrase}
        onChange={(e) => setDraft({ ...draft, phrase: e.target.value })}
      />
      <div className="mb-2 flex gap-1">
        {(['negative', 'positive'] as Polarity[]).map((p) => (
          <button
            key={p}
            onClick={() => setDraft({ ...draft, polarity: p })}
            className={`px-3 py-1 rounded text-xs font-medium border ${
              draft.polarity === p
                ? p === 'negative'
                  ? 'bg-red-600 text-white border-red-600'
                  : 'bg-green-600 text-white border-green-600'
                : 'bg-white text-ink border-softBorder hover:bg-gray-50'
            }`}
          >
            {p}
          </button>
        ))}
      </div>
      <textarea
        className="border rounded px-2 py-1 text-sm w-full mb-2 bg-white"
        rows={2}
        placeholder="Coaching note (shown to admins on a hit)"
        value={draft.light_bulb_text}
        onChange={(e) => setDraft({ ...draft, light_bulb_text: e.target.value })}
      />
      <div className="flex gap-2 justify-end">
        <button
          onClick={() => setOpen(false)}
          className="px-3 py-1 text-sm text-inkMuted hover:text-ink"
        >
          Cancel
        </button>
        <button
          onClick={handleCreate}
          disabled={!canSave || saving}
          className="px-3 py-1 rounded bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-40"
        >
          {saving ? 'Creating…' : 'Create'}
        </button>
      </div>
    </div>
  );
};

// ============================================================
// Page
// ============================================================

const PhraseRulesManagement: React.FC = () => {
  const { selectedTenant, tenantName } = useTenant();
  const [rules, setRules] = useState<PhraseRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!selectedTenant) return;
    setLoading(true);
    setError(null);
    try {
      const data = await listPhraseRules(selectedTenant);
      setRules(data.rules);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, [selectedTenant]);

  useEffect(() => {
    if (selectedTenant) reload();
    else setRules([]);
  }, [selectedTenant, reload]);

  return (
    <div className="min-h-screen bg-cream">
      <ScorecardNavigation />

      <div className="max-w-5xl mx-auto p-4 md:p-8">
        <div className="mb-8">
          <h1 className="text-4xl font-display font-semibold text-ink mb-1">Phrase Guidelines</h1>
          <p className="text-sm text-inkMuted">
            Phrases the AI watches for in{' '}
            <span className="font-semibold text-ink">{tenantName}</span>'s transcripts. Only the{' '}
            <span className="font-semibold text-ink">server's</span> speech is scanned — customer
            utterances are ignored. Paraphrase counts as a hit.
          </p>
        </div>

        {!selectedTenant && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded text-sm text-amber-900">
            No tenant selected. Return to a scorecard page and pick a tenant first.
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
            {error}
          </div>
        )}

        {loading && <div className="p-6 text-sm text-inkMuted">Loading phrase rules…</div>}

        {selectedTenant && !loading && (
          <div className="space-y-3">
            {rules.length === 0 && (
              <div className="p-6 text-sm text-inkMuted bg-white border border-softBorder rounded-lg text-center">
                No phrase rules yet. Add one below — it will be appended to the AI prompt
                immediately.
              </div>
            )}
            {rules.map((r) => (
              <RuleRow key={r.id} rule={r} onChanged={reload} />
            ))}
            <NewRuleForm tenantId={selectedTenant} onCreated={reload} />
          </div>
        )}
      </div>
    </div>
  );
};

export default PhraseRulesManagement;
